import { useRef, useState, useEffect } from 'react';
import type { GameLogEntry as GameLogEntryType } from '../types';
import { GameLogEntry } from './GameLogEntry';

interface GameLogProps {
  entries: GameLogEntryType[];
}

export function GameLog({ entries }: GameLogProps) {
  const [newEntryIds, setNewEntryIds] = useState<Set<string>>(new Set());
  const prevEntriesLengthRef = useRef(entries.length);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Handle new entries: highlight animation and scroll to top
  useEffect(() => {
    const prevLength = prevEntriesLengthRef.current;
    const currentLength = entries.length;

    if (currentLength > prevLength) {
      // Mark new entries for highlight animation
      const newIds = new Set<string>();
      for (let i = prevLength; i < currentLength; i++) {
        newIds.add(entries[i].id);
      }
      setNewEntryIds(newIds);

      // Clear highlight after delay
      const timeout = setTimeout(() => {
        setNewEntryIds(new Set());
      }, 1000);

      // Scroll to top - use multiple methods to ensure it works
      // Method 1: Immediate
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }

      // Method 2: After microtask (React flush)
      Promise.resolve().then(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
      });

      // Method 3: After next frame (paint complete)
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
        // Method 4: Double RAF for extra safety
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
          }
        });
      });

      prevEntriesLengthRef.current = currentLength;
      return () => clearTimeout(timeout);
    }

    prevEntriesLengthRef.current = currentLength;
  }, [entries]);

  // Reverse entries so newest appears first in the list
  const reversedEntries = [...entries].reverse();

  if (entries.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 h-full flex items-center justify-center">
        <div className="text-center text-gray-400">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p>Waiting for game action...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex-shrink-0">
        <h3 className="font-semibold text-gray-700 uppercase text-sm tracking-wide">
          Live Play-by-Play
        </h3>
        <p className="text-xs text-gray-400 mt-0.5">{entries.length} plays</p>
      </div>

      {/* Log entries - newest first */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto"
        style={{ overscrollBehavior: 'contain' }}
      >
        {reversedEntries.map((entry) => (
          <GameLogEntry
            key={entry.id}
            entry={entry}
            isNew={newEntryIds.has(entry.id)}
          />
        ))}
      </div>
    </div>
  );
}
